import { NgModule, inject } from '@angular/core';
import { Routes, RouterModule, Router, ActivatedRouteSnapshot } from '@angular/router';
import { PolicySnapshotView } from './policy-snapshot-view/policy-snapshot-view.component';
import { ProfileActivitiesViewComponent } from './profile-activities-view/profile-activities-view.component';
import { LoginComponent } from './login/login.component';
import { OKTA_AUTH, OktaCallbackComponent } from '@okta/okta-angular';
import { UserService } from './services/user.service';
import { ThirdPartyLoginComponent } from './third-party-login/third-party-login.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { CallIntentComponent } from './call-intent/call-intent.component';
import { PolicyDetailsService } from './services/policy-details.service';
import { SessionService } from './services/session.service';
import { Store } from '@ngrx/store';
import { loadPolicyDetailsSuccess } from './store/policy/policy.actions';
import { take } from 'rxjs/operators';
import { LoadingService } from './shared/loading.service';
import { TokenService } from './services/token.service';

const thirdPartyGuard = async (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    const store = inject(Store);
    const policyDetailsService = inject(PolicyDetailsService);
    const sessionService = inject(SessionService);
    const loadingService = inject(LoadingService);
    
    const sessionId = route.queryParams['sessionId'];
    
    if (!sessionId) {
        router.navigate(['/third-party-login']);
        return false;
    }

    // Persist the TP session so orchestrator calls can attach it as a header.
    sessionService.setSessionId(sessionId);
    
    return new Promise<boolean>((resolve) => {
        store.select((state: any) => state.policy.data).pipe(take(1)).subscribe((policyData) => {
            if (!policyData || Object.keys(policyData).length === 0) {
                const policyNumber = route.queryParams['policyNumber'];
                if (policyNumber) {
                    loadingService.showLoading();
                    const body = {
                        policyNumber,
                        requestType: 'getPolicySnapshotForTP',
                        sessionId: sessionId
                    };
                    policyDetailsService.getPolicyData(body).subscribe({
                        next: (data) => {
                            store.dispatch(loadPolicyDetailsSuccess({ data }));
                            loadingService.hideLoading();
                            resolve(true);
                        },
                        error: () => {
                            loadingService.hideLoading();
                            router.navigate(['/third-party-login'], { queryParams: { sessionId } });
                            resolve(false);
                        }
                    });
                } else {
                    resolve(true);
                }
            } else {
                resolve(true);
            }
        });
    });
};

const authGuard = async () => {
    const oktaAuth = inject(OKTA_AUTH);
    const userService = inject(UserService);
    const router = inject(Router);
    const tokenService = inject(TokenService);

    let isAuthenticated = await oktaAuth.isAuthenticated();

    if (isAuthenticated) {
        try {
            let tokens = await tokenService.getTokens(true);
            oktaAuth.tokenManager.setTokens(tokens);
            if (
                !userService.getUser().User_Name ||
                userService.getUser().User_Name != tokens?.idToken?.claims!.User_Name
            ) {
                userService.setUser(tokens?.idToken?.claims!);
            }
        } catch (error) {
            console.error('Silent SSO failed:', error);
            // fall through to /login
        }
    }

    // If the user already has an active Okta session
    //  get tokens without prompting for credentials 
    if (!isAuthenticated) {
        try {
            const hasSession = await oktaAuth.session.exists();
            if (hasSession) {
                const tokenResponse = await oktaAuth.token.getWithoutPrompt({
                    responseType: ['id_token', 'token'],
                    scopes: oktaAuth.options.scopes,
                });
                oktaAuth.tokenManager.setTokens(tokenResponse.tokens);
                isAuthenticated = await oktaAuth.isAuthenticated();
            }
        } catch (error) {
            // Fall through to the interactive login below.
            console.error('Auth Guard - Silent SSO failed:', error);
        }
    }

    if (!isAuthenticated) {
        router.navigate(['/login']);
        return false;
    }

    try {
        const oktaUser = await oktaAuth.getUser();
        if (oktaUser) {
            const userName = oktaUser.User_Name || oktaUser.preferred_username;
            const producerCode =
                (oktaUser as any)['custom:Producer_Code'] || (oktaUser as any)['custom:producer_code'] || '';

            userService.setUser({
                ...oktaUser,
                User_Name: userName,
                Producer_Code: producerCode,
            });
        }
    } catch (error) {
        console.error('Auth Guard - Failed to load user:', error);
    }

    return true;
};

export const routes: Routes = [
    {
        path: 'login/callback',
        component: OktaCallbackComponent,
    },
    { path: 'login', component: LoginComponent, title: 'login' },
    { path: 'third-party-login', component: ThirdPartyLoginComponent, title: 'Third Party Login' },
    { path: 'third-party', component: PolicySnapshotView, title: 'Third Party View' },
    {
        path: 'policy360',
        component: PolicySnapshotView,
        title: 'Policy Snapshot View',
        canActivate: [authGuard],
    },
    {
        path: 'policy360/third-party',
        component: PolicySnapshotView,
        title: 'TP-Policy Snapshot View',
        canActivate: [thirdPartyGuard],
    },
    {
        path: 'profile-activities',
        component: ProfileActivitiesViewComponent,
        title: 'Profile & Activities',
        canActivate: [authGuard],
    },
    {
        path: 'profile-activities/third-party',
        component: ProfileActivitiesViewComponent,
        title: 'TP-Profile & Activities',
        canActivate: [thirdPartyGuard],
    },
    {
        path: 'contact-us',
        component: ContactUsComponent,
        title: 'Contact Us',
    },

    {
        path: 'call-intent',
        component: CallIntentComponent,
        title: 'Call Intent',
        canActivate: [authGuard],
    },
    { path: '', redirectTo: 'profile-activities', pathMatch: 'full' },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {})],
    exports: [RouterModule],
})
export class AppRoutingModule {}
